import React, { useEffect, useState } from 'react';

import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import AddIcon from '@mui/icons-material/Add';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { Box, Button, Chip, Divider, IconButton, Paper, Stack, Typography } from '@mui/material';

import Image from '~/components/Common/Image';

import type { MediaSourceImage, PropertyRoomTypeWithImages } from './OfferBuilderImagesVideo';

const ROOM_IMAGE_SLOT_COUNT = 5;

interface Props {
  propertyRoomTypes?: Array<PropertyRoomTypeWithImages>;
}

interface RoomImage {
  publicId: string;
  title?: string;
}

function buildRoomImages(images: PropertyRoomTypeWithImages['images']): Array<RoomImage> {
  const seenPublicIds = new Set<string>();

  return (images ?? []).reduce<Array<RoomImage>>((roomImages, image: MediaSourceImage) => {
    const publicId = image.id_cloudinary_external;

    if (!publicId || seenPublicIds.has(publicId)) {
      return roomImages;
    }

    seenPublicIds.add(publicId);
    roomImages.push({
      publicId,
      title: image.title || undefined,
    });

    return roomImages;
  }, []);
}

export default function OfferBuilderImagesByRoom({ propertyRoomTypes = [] }: Props) {
  if (propertyRoomTypes.length === 0) {
    return null;
  }

  return (
    <Paper variant="outlined" sx={{ mt: 4, overflow: 'hidden' }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        spacing={1}
        sx={{ px: 3, py: 2.5 }}
      >
        <Typography variant="h5">Images by room</Typography>
        <Typography variant="body2" color="text.secondary">
          Each row matches a room in{' '}
          <Box component="span" sx={{ fontWeight: 700 }}>
            Rooms
          </Box>
          . Up to {ROOM_IMAGE_SLOT_COUNT} carousel slots.
        </Typography>
      </Stack>
      <Divider />

      <Stack divider={<Divider />} sx={{ px: 3 }}>
        {propertyRoomTypes.map((room, index) => (
          <RoomImageRow key={room.id || `${room.name || 'room'}-${index}`} room={room} />
        ))}
      </Stack>
    </Paper>
  );
}

function RoomImageRow({ room }: { room: PropertyRoomTypeWithImages }) {
  const [roomImages, setRoomImages] = useState<Array<RoomImage>>(() =>
    buildRoomImages(room.images).slice(0, ROOM_IMAGE_SLOT_COUNT),
  );
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  useEffect(() => {
    setRoomImages(buildRoomImages(room.images).slice(0, ROOM_IMAGE_SLOT_COUNT));
  }, [room.images]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    setRoomImages((currentRoomImages) => {
      const oldIndex = currentRoomImages.findIndex((image) => image.publicId === active.id);
      const newIndex = currentRoomImages.findIndex((image) => image.publicId === over.id);

      if (oldIndex < 0 || newIndex < 0) {
        return currentRoomImages;
      }

      return arrayMove(currentRoomImages, oldIndex, newIndex);
    });
  };

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', lg: '220px minmax(0, 1fr)' },
        gap: { xs: 2, lg: 3 },
        py: 3,
      }}
    >
      <Stack spacing={1.5} alignItems="flex-start">
        <Box>
          <Typography variant="h6">{room.name || 'Room'}</Typography>
          <Typography variant="body2" color="text.secondary">
            {roomImages.length} of {ROOM_IMAGE_SLOT_COUNT} slots filled
          </Typography>
        </Box>
        <Button
          variant="text"
          color="primary"
          startIcon={<AddIcon />}
          disabled={roomImages.length >= ROOM_IMAGE_SLOT_COUNT}
        >
          Pick images
        </Button>
      </Stack>

      <DndContext onDragEnd={handleDragEnd} sensors={sensors} collisionDetection={closestCenter}>
        <SortableContext items={roomImages.map((image) => image.publicId)} strategy={rectSortingStrategy}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(2, minmax(0, 1fr))',
                sm: 'repeat(3, minmax(0, 1fr))',
                xl: `repeat(${ROOM_IMAGE_SLOT_COUNT}, minmax(0, 1fr))`,
              },
              gap: 2,
            }}
          >
            {Array.from({ length: ROOM_IMAGE_SLOT_COUNT }, (_value, index) => (
              <RoomImageSlot
                key={roomImages[index]?.publicId ?? index}
                image={roomImages[index]}
                slotNumber={index + 1}
              />
            ))}
          </Box>
        </SortableContext>
      </DndContext>
    </Box>
  );
}

function RoomImageSlot({ image, slotNumber }: { image?: RoomImage; slotNumber: number }) {
  const slotLabel = slotNumber === 1 ? `#${slotNumber} Hero` : `#${slotNumber}`;

  if (image) {
    return <SortableRoomImageSlot image={image} slotLabel={slotLabel} />;
  }

  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        aspectRatio: '4 / 3',
        width: '100%',
        border: 1,
        borderColor: 'grey.300',
        borderRadius: 1,
        bgcolor: 'grey.50',
      }}
    >
      <Stack alignItems="center" justifyContent="center" sx={{ width: '100%', height: '100%', color: 'grey.400' }}>
        <AddPhotoAlternateOutlinedIcon fontSize="large" />
      </Stack>
      <Chip label={slotLabel} size="small" sx={roomImageSlotChipSx} />
    </Box>
  );
}

function SortableRoomImageSlot({ image, slotLabel }: { image: RoomImage; slotLabel: string }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: image.publicId });

  return (
    <Box
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        aspectRatio: '4 / 3',
        width: '100%',
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'background.default',
        cursor: isDragging ? 'grabbing' : 'grab',
        opacity: isDragging ? 0.72 : 1,
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 2 : 'auto',
        '&:hover .OfferBuilderImagesByRoom-overlay, &:focus-within .OfferBuilderImagesByRoom-overlay': {
          opacity: 1,
        },
      }}
    >
      <Image
        publicId={image.publicId}
        alt={image.title || 'Room image'}
        options={{ width: 320, height: 240 }}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          display: 'block',
        }}
      />
      <Chip label={slotLabel} size="small" sx={roomImageSlotChipSx} />
      <Stack
        className="OfferBuilderImagesByRoom-overlay"
        direction="row"
        alignItems="flex-end"
        justifyContent="flex-end"
        spacing={0.75}
        sx={roomImageOverlaySx}
        onPointerDown={(event) => event.stopPropagation()}
      >
        <IconButton aria-label="Edit room image" sx={imageActionButtonSx}>
          <EditIcon fontSize="small" />
        </IconButton>
        <IconButton aria-label="Hide room image" sx={imageActionButtonSx}>
          <VisibilityOffIcon fontSize="small" />
        </IconButton>
        <IconButton aria-label="Delete room image" sx={imageActionButtonSx}>
          <DeleteIcon color="error" fontSize="small" />
        </IconButton>
      </Stack>
    </Box>
  );
}

const imageActionButtonSx = {
  width: 34,
  height: 34,
  bgcolor: 'common.white',
  color: 'grey.800',
  '&:hover': {
    bgcolor: 'grey.100',
  },
};

const roomImageSlotChipSx = {
  position: 'absolute',
  top: 10,
  left: 10,
  bgcolor: 'grey.800',
  color: 'common.white',
  fontWeight: 700,
};

const roomImageOverlaySx = {
  position: 'absolute',
  right: 0,
  bottom: 0,
  left: 0,
  opacity: 0,
  transition: 'opacity 160ms ease-in-out',
  px: 1.25,
  py: 1,
  background: 'linear-gradient(180deg, rgba(0, 0, 0, 0), rgba(0, 0, 0, 0.62))',
};
